// src/components/LessonNotesSidebar.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../config/supabase';
import { format, parseISO } from 'date-fns';

const {
  FiFileText,
  FiChevronLeft,
  FiChevronRight,
  FiUser,
  FiCalendar,
  FiMusic,
  FiBookOpen,
  FiClock
} = FiIcons;

/**
 * Props
 * - currentNoteId (uuid)
 * - studentId? (uuid)   → student mode (existing behavior)
 * - groupId? (uuid)     → NEW: group mode (one note per lesson; show other notes for the group)
 * - onNoteSelect(id)
 * - onEventSelect(event|null)
 * - selectedEventId
 * - isCollapsed
 * - onToggleCollapse()
 * - hideUpcomingEvents (boolean)
 */
const LessonNotesSidebar = ({
  currentNoteId,
  studentId,
  groupId,              // NEW: enables group flow when studentId is not provided
  onNoteSelect,
  onEventSelect,
  selectedEventId,
  isCollapsed,
  onToggleCollapse,
  hideUpcomingEvents = false,
}) => {
  const [lessonNotes, setLessonNotes] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [studentName, setStudentName] = useState('');
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextLessonDateTime, setNextLessonDateTime] = useState(null);

  // Decide mode: prefer student if provided; else group
  const inStudentMode = !!studentId && !groupId;
  const inGroupMode = !studentId && !!groupId;

    // Fetch group header/title ONLY when the note changes
  useEffect(() => {
    if (!inGroupMode || !currentNoteId) return;

    let alive = true;

    const fetchGroupHeaderFromCurrentNote = async (noteId) => {
      try {
        // Resolve lesson_id from the note
        const { data: ln } = await supabase
          .from('lesson_notes')
          .select('lesson_id')
          .eq('id', noteId)
          .maybeSingle();

        if (!alive || !ln?.lesson_id) return;

        // Get the lesson title from calendar_events
        const { data: ev } = await supabase
          .from('calendar_events')
          .select('title')
          .eq('linked_table', 'lessons')
          .eq('linked_id', ln.lesson_id)
          .eq('event_type', 'lesson')
          .order('start_time', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!alive) return;

        setStudentName(ev?.title || 'Group');
      } catch {
        if (alive) setStudentName('Group');
      }
    };

    fetchGroupHeaderFromCurrentNote(currentNoteId);

    return () => {
      alive = false;
    };
  }, [currentNoteId, inGroupMode]);

  useEffect(() => {
    setError(null);
    setNextLessonDateTime(null);

    if (inStudentMode) {
      fetchStudentAndNotes();
      if (!hideUpcomingEvents) fetchUpcomingEvents();
      fetchNextLessonForStudent();
    } else if (inGroupMode) {
      fetchGroupAndNotes();
      fetchNextLessonForGroup();
      // Events are student-specific; in group mode leave empty
      setEventsLoading(false);
    } else {
      // Nothing to load
      setLoading(false);
      setEventsLoading(false);
    }
  }, [studentId, groupId]);

  // ────────────────────────────────────────────────────────────────────────────
  // STUDENT MODE (existing behavior)

  const fetchStudentAndNotes = async () => {
    try {
      setLoading(lessonNotes.length === 0);

      const { data: studentData, error: studentError } = await supabase
        .from('students')
        .select('name')
        .eq('id', studentId)
        .single();

      if (studentError) {
        console.error('Error fetching student:', studentError);
        setStudentName('Unknown Student');
      } else {
        setStudentName(studentData?.name || 'Unknown Student');
      }

      const { data: notesData, error: notesError } = await supabase
        .from('lesson_notes')
        .select('id, title, created_at, updated_at, lesson_datetime')
        .eq('student_id', studentId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (notesError) {
        console.error('Error fetching lesson notes:', notesError);
        setError('Failed to load lesson notes');
      } else {
        setLessonNotes(notesData || []);
      }
    } catch (err) {
      console.error('Error in fetchStudentAndNotes:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchNextLessonForStudent = async () => {
    try {
      const nowIso = new Date().toISOString();
      const { data, error } = await supabase
        .from('lessons')
        .select('id, student_id, status, scheduled_time')
        .eq('student_id', studentId)
        .gte('scheduled_time', nowIso)
        .order('scheduled_time', { ascending: true })
        .limit(1);

      if (error) {
        console.error('Error fetching next student lesson:', error);
        setNextLessonDateTime(null);
        return;
      }

      if (data && data.length > 0 && data[0]?.scheduled_time) {
        setNextLessonDateTime(parseISO(data[0].scheduled_time));
      } else {
        setNextLessonDateTime(null);
      }
    } catch (e) {
      console.error('Error in fetchNextLessonForStudent:', e);
      setNextLessonDateTime(null);
    }
  };

  const fetchUpcomingEvents = async () => {
    try {
      setEventsLoading(true);
      const today = new Date().toISOString().split('T')[0];

      const { data: performanceStudentData } = await supabase
        .from('performance_students')
        .select('id, performance_id')
        .eq('student_id', studentId);

      const { data: examStudentData } = await supabase
        .from('exam_students')
        .select('id, exam_id, scheduled_date, scheduled_time, location')
        .eq('student_id', studentId);

      const events = [];

      if (performanceStudentData?.length) {
        const pIds = performanceStudentData.map(ps => ps.performance_id);
        const { data: performancesData } = await supabase
          .from('performances')
          .select('id, title, performance_date, performance_time, location, description')
          .in('id', pIds)
          .gte('performance_date', today)
          .order('performance_date', { ascending: true });

        performancesData?.forEach(perf => {
          const studentRecord = performanceStudentData.find(ps => ps.performance_id === perf.id);
          if (!studentRecord) return;
          events.push({
            id: `performance-${studentRecord.id}`,
            type: 'performance',
            data: {
              id: perf.id,
              name: perf.title,
              date: perf.performance_date,
              time: perf.performance_time,
              location: perf.location,
              description: perf.description
            },
            studentRecordId: studentRecord.id,
            date: perf.performance_date,
            time: perf.performance_time
          });
        });
      }

      if (examStudentData?.length) {
        const eIds = examStudentData.map(es => es.exam_id);
        const { data: examsData } = await supabase
          .from('exams')
          .select('id, name, session, opening_date, closing_date, location, notes')
          .in('id', eIds)
          .order('opening_date', { ascending: true });

        examsData?.forEach(exam => {
          const es = examStudentData.find(x => x.exam_id === exam.id);
          if (!es) return;
          const eventDate = es.scheduled_date || exam.opening_date;
          const eventTime = es.scheduled_time;
          const eventLocation = es.location || exam.location;
          if (eventDate && eventDate >= today) {
            events.push({
              id: `exam-${es.id}`,
              type: 'exam',
              data: {
                id: exam.id,
                name: exam.name,
                session: exam.session,
                date: eventDate,
                time: eventTime,
                location: eventLocation,
                description: exam.notes,
                opening_date: exam.opening_date,
                closing_date: exam.closing_date
              },
              studentRecordId: es.id,
              date: eventDate,
              time: eventTime
            });
          }
        });
      }

      events.sort((a, b) => {
        const aDt = new Date(`${a.date}T${a.time || '00:00'}`).getTime();
        const bDt = new Date(`${b.date}T${b.time || '00:00'}`).getTime();
        return aDt - bDt;
      });

      setUpcomingEvents(events);
    } catch (err) {
      console.error('Error fetching upcoming events:', err);
    } finally {
      setEventsLoading(false);
    }
  };

  // ────────────────────────────────────────────────────────────────────────────
  // GROUP MODE (new)

  const fetchGroupAndNotes = async () => {
    try {
      setLoading(lessonNotes.length === 0);

      // 1) Group name (parent-safe): derive from calendar_events of THIS lesson
      // First, resolve the current note's lesson_id
      
      // 2) All lessons for this group (newest first)
      const { data: lsns, error: lsErr } = await supabase
        .from('lessons')
        .select('id, scheduled_time')
        .eq('group_id', groupId)
        .order('scheduled_time', { ascending: false })
        .limit(200);
      if (lsErr) throw lsErr;

      const lessonIds = (lsns ?? []).map(l => l.id);
      if (!lessonIds.length) {
        setLessonNotes([]);
        return;
      }

      // 3) Notes for those lessons (one per lesson by design)
      const { data: notesData, error: notesErr } = await supabase
        .from('lesson_notes')
        .select('id, title, created_at, updated_at, lesson_datetime, lesson_id')
        .in('lesson_id', lessonIds)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (notesErr) {
        console.error('Error fetching group lesson notes:', notesErr);
        setError('Failed to load lesson notes');
      } else {
        setLessonNotes(notesData || []);
      }
    } catch (err) {
      console.error('Error in fetchGroupAndNotes:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchNextLessonForGroup = async () => {
    try {
      const nowIso = new Date().toISOString();
      const { data, error } = await supabase
        .from('lessons')
        .select('id, group_id, status, scheduled_time')
        .eq('group_id', groupId)
        .gte('scheduled_time', nowIso)
        .order('scheduled_time', { ascending: true })
        .limit(1);

      if (error) {
        console.error('Error fetching next group lesson:', error);
        setNextLessonDateTime(null);
        return;
      }

      if (data && data.length > 0 && data[0]?.scheduled_time) {
        setNextLessonDateTime(parseISO(data[0].scheduled_time));
      } else {
        setNextLessonDateTime(null);
      }
    } catch (e) {
      console.error('Error in fetchNextLessonForGroup:', e);
      setNextLessonDateTime(null);
    }
  };

  // ────────────────────────────────────────────────────────────────────────────
  // Helpers

  const formatEventDate = (dateString, timeString) => {
    try {
      const date = parseISO(dateString);
      const d = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(date);
      if (timeString) {
        const [h, m] = timeString.split(':').map(Number);
        const t = new Date();
        t.setHours(h || 0, m || 0, 0, 0);
        const tm = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(t);
        return `${d} at ${tm}`;
      }
      return d;
    } catch {
      return 'Unknown Date';
    }
  };

  const handleNoteClick = (noteId) => {
    if (onNoteSelect && noteId !== currentNoteId) onNoteSelect(noteId);
  };

  const handleEventClick = (event) => {
    if (!onEventSelect) return;
    if (selectedEventId && selectedEventId === event.id) onEventSelect(null);
    else onEventSelect(event);
  };

  // ────────────────────────────────────────────────────────────────────────────
  // Render

  // Loading skeleton
  if (loading) {
    return (
      <div className={`bg-gray-50 border-r border-gray-200 flex flex-col transition-all duration-300 ${isCollapsed ? 'w-12' : 'w-80'}`}>
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          {!isCollapsed && (
            <div className="flex items-center animate-pulse">
              <div className="w-6 h-6 bg-gray-300 rounded mr-2"></div>
              <div className="w-32 h-4 bg-gray-300 rounded"></div>
            </div>
          )}
          <button
            onClick={onToggleCollapse}
            className="p-1 hover:bg-gray-200 rounded transition-colors"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <SafeIcon icon={isCollapsed ? FiChevronRight : FiChevronLeft} className="text-gray-600" />
          </button>
        </div>

        {!isCollapsed && (
          <div className="flex-1 p-4">
            {/* Notes list skeleton */}
            <div className="space-y-2 mb-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="w-full h-12 bg-gray-200 rounded animate-pulse"></div>
              ))}
            </div>

            {/* Upcoming events header */}
            <div className="h-5 w-36 bg-gray-200 rounded animate-pulse mb-2" />
            {/* Upcoming events items */}
            <div className="space-y-2">
              <div className="h-10 bg-gray-200 rounded animate-pulse" />
              <div className="h-10 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`bg-gray-50 border-r border-gray-200 flex flex-col transition-all duration-300 ${isCollapsed ? 'w-12' : 'w-80'}`}>
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          {!isCollapsed && (
            <div className="flex items-center text-red-600">
              <SafeIcon icon={FiUser} className="mr-2" />
              <span className="text-sm font-medium">Error</span>
            </div>
          )}
          <button
            onClick={onToggleCollapse}
            className="p-1 hover:bg-gray-200 rounded transition-colors"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <SafeIcon icon={isCollapsed ? FiChevronRight : FiChevronLeft} className="text-gray-600" />
          </button>
        </div>

        {!isCollapsed && (
          <div className="flex-1 p-4 flex items-center justify-center">
            <p className="text-red-600 text-sm text-center">{error}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`bg-gray-50 border-r border-gray-200 flex flex-col transition-all duration-300 ${isCollapsed ? 'w-12' : 'w-80'}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <AnimatePresence>
          {!isCollapsed && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="flex items-center min-w-0 flex-1"
            >
              <SafeIcon icon={FiUser} className="text-blue-600 mr-2 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-semibold text-gray-900 truncate">
                  {studentName || (inGroupMode ? 'Group' : 'Student')}
                </h2>
                <p className="text-sm text-gray-500">Lessons</p>
                {nextLessonDateTime && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Next lesson time: {new Intl.DateTimeFormat(undefined, {
                      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
                      hour: 'numeric', minute: '2-digit'
                    }).format(nextLessonDateTime)}
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          onClick={onToggleCollapse}
          className="p-1 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <SafeIcon icon={isCollapsed ? FiChevronRight : FiChevronLeft} className="text-gray-600" />
        </button>
      </div>

      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex-1 flex flex-col overflow-hidden"
          >
            {/* Lesson Notes Section - Upper 2/3 */}
            <div className="flex-2 overflow-y-auto" style={{ flex: '2' }}>
              {lessonNotes.length === 0 ? (
                <div className="p-4 text-center">
                  <SafeIcon icon={FiFileText} className="text-gray-400 text-2xl mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No lesson notes found</p>
                </div>
              ) : (
                <div className="p-2">
                  {lessonNotes.map((note) => (
                    <motion.button
                      key={note.id}
                      onClick={() => handleNoteClick(note.id)}
                      className={`w-full text-left p-3 rounded-lg mb-2 transition-all duration-200 ${
                        note.id === currentNoteId
                          ? 'bg-blue-100 border-l-4 border-blue-500 shadow-sm'
                          : 'hover:bg-gray-100 border-l-4 border-transparent'
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-start">
                        <SafeIcon
                          icon={FiFileText}
                          className={`mr-3 mt-0.5 flex-shrink-0 ${
                            note.id === currentNoteId ? 'text-blue-600' : 'text-gray-400'
                          }`}
                        />
                        <div className="min-w-0 flex-1">
                          <h3 className={`font-medium text-sm mb-0.5 truncate ${
                            note.id === currentNoteId ? 'text-blue-900' : 'text-gray-900'
                          }`}>
                            {format(parseISO(note.lesson_datetime || note.created_at), 'EEEE do MMMM yyyy')}
                          </h3>
                          {note.lesson_datetime && (
                            <div className="text-xs text-gray-500">
                              {new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' })
                                .format(parseISO(note.lesson_datetime))}
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </div>
              )}
            </div>

            {/* Upcoming Events Section - Lower 1/3 */}
            {!hideUpcomingEvents && (
              <div className="flex-1 border-t border-gray-200 flex flex-col">
              <div className="p-3 bg-gray-100">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center">
                  <SafeIcon icon={FiClock} className="mr-2 text-orange-500" />
                  Upcoming Events
                </h3>
              </div>
              
              <div className="flex-1 overflow-y-auto">
                {eventsLoading ? (
                  <div className="p-3">
                    <div className="space-y-2">
                      {[1, 2].map(i => (
                        <div key={i} className="w-full h-10 bg-gray-200 rounded animate-pulse"></div>
                      ))}
                    </div>
                  </div>
                ) : upcomingEvents.length === 0 ? (
                  <div className="p-3 text-center">
                    <SafeIcon icon={FiCalendar} className="text-gray-400 text-lg mx-auto mb-1" />
                    <p className="text-gray-500 text-xs">
                      {inGroupMode ? 'No group-level events' : 'No upcoming events'}
                    </p>
                  </div>
                ) : (
                  <div className="p-2">
                    {upcomingEvents.map((event) => (
                      <motion.button
                        key={event.id}
                        onClick={() => handleEventClick(event)}
                        aria-pressed={selectedEventId === event.id}
                        className={`w-full text-left p-2 rounded-md mb-1 transition-colors ${
                          selectedEventId === event.id ? 'bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-gray-100'
                        }`}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        <div className="flex items-start">
                          <SafeIcon
                            icon={event.type === 'performance' ? FiMusic : FiBookOpen}
                            className={`mr-2 mt-0.5 flex-shrink-0 ${
                              event.type === 'performance' ? 'text-purple-500' : 'text-green-500'
                            }`}
                          />
                          <div className="min-w-0 flex-1">
                            <h4 className="font-medium text-xs mb-0.5 truncate text-gray-900">
                              {event.data.name}
                              {event.type === 'exam' && event.data.session && (
                                <span className="text-gray-500"> - {event.data.session}</span>
                              )}
                            </h4>
                            <div className="text-xs text-gray-500 mb-0.5">
                              {formatEventDate(event.date, event.time)}
                            </div>
                            {event.data.location && (
                              <div className="text-xs text-gray-400 truncate">
                                📍 {event.data.location}
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                )}
              </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Collapsed state indicator */}
      {isCollapsed && currentNoteId && (
        <div className="flex-1 flex flex-col items-center justify-center space-y-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
          {upcomingEvents.length > 0 && (
            <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
          )}
        </div>
      )}
    </div>
  );
};

export default LessonNotesSidebar;